import React, { useEffect, useRef, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "react-md";
import { MSpinner } from "../Shared/index.js";
import MDialog from "components/Shared/MDialog.tsx";
import MGridForm from "components/Shared/MGridForm";
import { DATA_TYPE_DROPDOWN, DATA_TYPE_MULTI_LINE_STRING, DATA_TYPE_PHONE_NUMBER, DATA_TYPE_STRING } from "typeConstants/dataTypes";
import { personOptions, insuranceReasonOptions, valuationReasonOptions, statusOptions, errorOptions } from "typeConstants/customerService";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { NEW_LENDER, EXISTING_LENDER } from "typeConstants/customerTypes";
import IncidentAttachments from "./IncidentAttachments.tsx";
import IncidentAdditionalNotes from "./IncidentAdditionalNotes.js";
import { addIncidentAttachmentToExisting, addPendingIncidentAttachment } from "requests/customerService";
import { CustomerServiceDeptOptions } from "typeConstants/employeeDepartments.ts";
import { CustomerServiceAssignToUser, CustomerServiceIncidentAdd, CustomerServiceIncidentAttachmentMetadata, CustomerServiceIncidentUpdate } from "interfaces/Mueller.Business.Models.ts";
import { ClientSideIncident } from "./ClientSideIncident.ts";

interface Props {
  incident: ClientSideIncident;                               // The incident to edit, or a new incident to add.... this is a mess inherited from javascript
  title: string;                                              // Title shown in dialog header                            // Use to show/hide dialog
  hideIncidentDialog: () => void;                             // This should set incidentDialogVisible to false, called when the user clicks outside of the dialog or clicks cancel
  onSubmitUpdate: (dto: CustomerServiceIncidentUpdate) => void; // Called when the user clicks the Save button when editing
  onSubmitCreate: (dto: CustomerServiceIncidentAdd) => void;    // Called when the user clicks the Save button when creating
  isFetchingAssignToUsers: boolean;                           // Used to determine if a spinner or the dialog should be shown
  controlNumber: number;                                      // Displayed in the dialog for user reference
  assignToUsers: CustomerServiceAssignToUser[];               // Options for Assigned To dialog
  canClose: boolean;                                          // If true, the user will be able to change the incident status
  canDelete: boolean;                                         // If true, the user will be shown the delete button
  onDeleteClicked: () => void;                                // Function to be called when delete button is clicked
  handleFormChange: any; // TODO:def                          // Function that MGridForm will pass an object that represents the data passed in only with changed values, the source of the last field changed, the dataType of the last field changed, the value of the last field changed value, an array of all of the sources changed so far
  customerType: string;                                       // The type of customer for the current control number, used to determine which reasons list to display
}

const AddEditIncidentDialog = (props: Props) => {
  const mounted = useRef<boolean>(true);
  const formControlMethods = useRef<any>({});

  const getUsersForDept = deptCode => {
    return props.assignToUsers.filter(u =>u.departmentCode === deptCode);
  };

  const setUsersForDept = deptCode => {
    const users = getUsersForDept(deptCode);
    setFilteredAssignToUsers(users);
  };

  const [incidentId, setIncidentId] = useState<number>(null);
  const [attachments, setAttachments] = useState<CustomerServiceIncidentAttachmentMetadata[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [filteredAssignToUsers, setFilteredAssignToUsers] = useState(
    props.incident.assignedDepartment 
    ? getUsersForDept(props.incident.assignedDepartment)
    : []
  );

  useEffect(() => {
    return (() => { mounted.current = false; });
  }, []);

  useEffect(() => {
    if(props.incident.id !== incidentId) {
      setIncidentId(props.incident.id);
      setAttachments(props.incident.attachments?.slice(0)); // using a copy so we don't violate the 'immutable props' principle
      setUsersForDept(props.incident.assignedDepartment);
    }      
  }, [props.incident.id]);

  useEffect(() => {
    if (props.incident.assignedDepartment) {
      setUsersForDept(props.incident.assignedDepartment);
    } else {
      setFilteredAssignToUsers([]); 
    }
  }, [props.incident.assignedDepartment]);

  const { getRootProps, isDragAccept } = useDropzone({
    noClick: true,
    noKeyboard: true,
    onDrop: files => {      

      setIsUploading(true);
      files[0].arrayBuffer().then(ab => {
        
        const successCallback = (data) => {
          const nextAttachments = attachments.slice(0);
          nextAttachments.push(data);
          onAttachmentsChanged(nextAttachments);
          setIsUploading(false);
        };
        const errorCallback = () => {
          setIsUploading(false);
        };

        if (props.incident.id) {
          addIncidentAttachmentToExisting(files[0].name, props.incident.id, ab, successCallback, errorCallback, mounted);
        }
        else {
          addPendingIncidentAttachment(files[0].name, ab, successCallback, errorCallback, mounted);
        }
      });
    },
  });

  const onAttachmentsChanged = (nextAttachments) => {
    setAttachments(nextAttachments);
  };

  const onSubmit = () => {
    // have to explicitly set null for these to pass backend validation
    const replyEmail = props.incident.replyToEmail?.trim() ? props.incident.replyToEmail.trim() : null; 
    const replyPhone = props.incident.replyToPhone?.trim() ? props.incident.replyToPhone.trim() : null;

    if (props.incident.id) {
      const update: CustomerServiceIncidentUpdate = {
        ...props.incident,
        assignedInternetUserId: parseInt(props.incident.assignedInternetUserId),
        errorOption: parseInt(props.incident.errorOption),
        replyToEmail: replyEmail,
        replyToPhone: replyPhone,
      };
      props.onSubmitUpdate(update);
    }
    else {
      const create: CustomerServiceIncidentAdd = {
        ...props.incident,
        createdByUserId: 0,
        assignedInternetUserId: parseInt(props.incident.assignedInternetUserId),
        errorOption: parseInt(props.incident.errorOption),
        attachments: attachments.map(a => a.lookupKey),
        replyToEmail: replyEmail,
        replyToPhone: replyPhone,
      };
      props.onSubmitCreate(create);
    }
  };

  const getDialogActions = () => {
    let actions = [];
    actions.push(<Button primary raised onClick={() => { if (formControlMethods.current.isFormValid()) onSubmit(); }}>Save</Button>);
    if (props.canDelete && props.incident.id) actions.push({ secondary: false, children: "Delete", onClick: props.onDeleteClicked });
    actions.push({ secondary: false, children: "Cancel", onClick: props.hideIncidentDialog });
    return actions;
  };

  const reasonsList = props.customerType === NEW_LENDER || props.customerType === EXISTING_LENDER ? valuationReasonOptions : insuranceReasonOptions;

  const mGridFormDefinition: any[] = [
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Status", source: "status", dataTypeOptions: { options: statusOptions }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Department", source: "assignedDepartment", dataTypeOptions: { options: CustomerServiceDeptOptions }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Assigned To", source: "assignedInternetUserId", dataTypeOptions: { options: filteredAssignToUsers } }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Person Interviewed", source: "personTypeCode", dataTypeOptions: { options: personOptions }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Reply To Name", source: "replyToName" }
    ],
    [
      { dataType: DATA_TYPE_STRING, label: "Reply To Email", source: "replyToEmail", hasValidationError: validator.emailAddress }
    ],
    [
      { dataType: DATA_TYPE_PHONE_NUMBER, label: "Reply To Phone", source: "replyToPhone", hasValidationError: validator.phoneNumber }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Error Found", source: "errorOption", dataTypeOptions: { options: errorOptions } }
    ],
    [
      { dataType: DATA_TYPE_DROPDOWN, label: "Reason", source: "reasonTypeCode", dataTypeOptions: { options: reasonsList }, hasValidationError: validator.required }
    ],
    [
      { dataType: DATA_TYPE_MULTI_LINE_STRING, label: "Initial Note", source: "note", dataTypeOptions: { defaultLines: 5 }, hasValidationError: validator.required }
    ],
  ];

  if (props.incident.id) { // if is editing
    mGridFormDefinition.unshift([{ label: `Incident # ${props.incident.id}` }]);
  }
  
  mGridFormDefinition.unshift([
    { label: `Control # ${props.controlNumber}` }
  ]);

  const dropZoneBaseStyle = {
    border: "2px dashed transparent",
  };

  const dropZoneAcceptStyle = {
    border: "2px dashed red",
  };

  const activeStyle = useMemo(() => ({
    ...dropZoneBaseStyle,
    ...(isDragAccept ? dropZoneAcceptStyle : {}),
  }), [isDragAccept]);

  return (
    <MDialog
      id="cs-add-edit-incident"
      className="cs-add-edit-incident-dialog"
      title={props.title}
      contentActions={getDialogActions()}
      focusOnMount={false}
      width={625}
    >
      {props.isFetchingAssignToUsers && <MSpinner />}
      <div {...getRootProps({style: activeStyle})}>
        {!props.isFetchingAssignToUsers &&
          <MGridForm
            width={600}
            data={props.incident}
            onChange={props.handleFormChange}
            formDefinition={mGridFormDefinition}
            portalTarget={() => document.querySelector(".cs-add-edit-incident-dialog")}
            getControlMethods={cm => { formControlMethods.current = cm; }}
          />
        }
        {!props.isFetchingAssignToUsers &&
          <IncidentAttachments 
            incidentId={props.incident.id}
            attachments={attachments}
            onAttachmentsChanged={onAttachmentsChanged}
            disabled={isUploading}
          />
        }
        {props.incident.id && <IncidentAdditionalNotes 
          incident={props.incident}
        />}        
      </div>
    </MDialog>
  ); 
};

export default AddEditIncidentDialog;